import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createRoute, Navigate } from '@tanstack/react-router'
import { Plus, Search, Trash2 } from 'lucide-react'
import { type FormEvent, useState } from 'react'
import {
  BulkActionBar,
  EmptyTable,
  Pager,
  RowCheckbox,
  SelectAllCheckbox,
  SortableHeader,
  TableToolbar,
} from '../components/data-table'
import { MobileBottomSlots, useMobilePageTitle } from '../components/shell/mobile-shell-outlets'
import { Button } from '../components/ui/button'
import { confirm } from '../components/ui/confirm-dialog'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { BarAccentPlus, BarSlot } from '../components/ui/mobile-action-bar'
import { PageHeader } from '../components/ui/page-header'
import { SidePanel } from '../components/ui/side-panel'
import { TableShell } from '../components/ui/table-shell'
import { api, type Tag } from '../lib/api'
import { useDataTable } from '../lib/data-table'
import { notify } from '../lib/notify'
import { Route as rootRoute } from './__root'

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/tags',
  component: TagsPage,
})

export function TagsPage({ embedded = false }: { embedded?: boolean } = {}) {
  const [createOpen, setCreateOpen] = useState(false)
  // Mobile-Shell (<640): Suche-Sheet hinterm Bottom-Slot; Titel nur
  // standalone — eingebettet (/stammdaten-Tab) gehört er der Host-Seite.
  const [searchSheet, setSearchSheet] = useState(false)
  useMobilePageTitle(embedded ? null : 'Tags')
  const { data: meData, isLoading: meLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.me(),
  })
  const { data, isLoading } = useQuery({
    queryKey: ['tags'],
    queryFn: () => api.listTags(),
    enabled: !!meData?.user,
  })

  const tags = data?.tags ?? []
  const table = useDataTable<Tag>(
    tags,
    { name: 'name', color: 'color', createdAt: (t) => new Date(t.createdAt) },
    {
      searchableFields: ['name', 'color'],
      defaultSortKey: 'name',
      defaultSortDir: 'asc',
      defaultPageSize: 50,
      storageKey: 'tags',
    },
  )

  const queryClient = useQueryClient()
  const bulkDelete = useMutation({
    mutationFn: (ids: string[]) => api.bulkDeleteTags(ids),
    onSuccess: (r) => {
      table.clearSelection()
      queryClient.invalidateQueries({ queryKey: ['tags'] })
      notify.success(`${r.deleted} Tags gelöscht`)
    },
    onError: (err: Error) => notify.error(err.message),
  })

  if (meLoading) return null
  if (!meData?.user) return <Navigate to="/login" />
  const isAdmin = meData.user.role === 'admin'

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Shell-Bottom-Slots (Phone + Tablet-Band): Suche → Sheet + ＋ Neuer Tag —
          Handler identisch zu den Kopf-/Toolbar-Controls darunter. */}
      <MobileBottomSlots>
        <BarSlot
          label="Suche"
          icon={<Search className="h-5 w-5" />}
          active={table.search !== ''}
          ariaLabel="Tags durchsuchen"
          ariaHasPopup
          onClick={() => setSearchSheet(true)}
        />
        <BarAccentPlus ariaLabel="Neuer Tag" onClick={() => setCreateOpen(true)} />
      </MobileBottomSlots>

      {/* Suche-Sheet (<640): bindet dieselbe table.search wie die
          Toolbar-Suche — Treffer filtern live hinter dem Sheet. */}
      <SidePanel open={searchSheet} onClose={() => setSearchSheet(false)} title="Suche">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-text-tertiary" />
          <Input
            value={table.search}
            onChange={(e) => table.setSearch(e.target.value)}
            placeholder="Tags durchsuchen …"
            className="pl-8"
            autoFocus
          />
        </div>
      </SidePanel>

      {/* In-Content-Kopf: <640 übernimmt die Shell (Titel oben, Aktionen
          unten) — phone:hidden (Basis sichtbar, deckt Hoch- + Querformat ab). */}
      <PageHeader
        title="Tags"
        actions={
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="h-3.5 w-3.5" /> Neuer Tag
          </Button>
        }
      />

      <TableToolbar
        search={table.search}
        onSearch={table.setSearch}
        filteredRows={table.filteredRows}
        totalRows={table.totalRows}
        pageSize={table.pageSize}
        onPageSize={table.setPageSize}
      />

      <TableShell className="rounded-2xl border-border-subtle bg-bg-elevated/40">
        {isLoading ? (
          <div className="px-6 py-12 text-center text-text-secondary">Lade …</div>
        ) : tags.length === 0 ? (
          <EmptyTable
            message="Noch keine Tags."
            hint="Tags helfen dir, Einträge thematisch zu gruppieren."
          />
        ) : table.rows.length === 0 ? (
          <EmptyTable message="Keine Treffer." hint="Suche oder Filter anpassen." />
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-bg-overlay/40">
              <tr className="text-left">
                {isAdmin && (
                  <th className="pl-5 pr-2 py-2.5 w-10">
                    <SelectAllCheckbox
                      checked={table.allOnPageSelected}
                      indeterminate={table.someOnPageSelected}
                      onToggle={table.toggleAllOnPage}
                    />
                  </th>
                )}
                <th className="px-5 py-2.5 w-10" aria-label="Farbe" />
                <th className="px-5 py-2.5">
                  <SortableHeader
                    label="Name"
                    sortKey="name"
                    currentKey={table.sortKey}
                    currentDir={table.sortDir}
                    onSort={table.toggleSort}
                  />
                </th>
                <th className="px-5 py-2.5">
                  <SortableHeader
                    label="Farbe (Hex)"
                    sortKey="color"
                    currentKey={table.sortKey}
                    currentDir={table.sortDir}
                    onSort={table.toggleSort}
                  />
                </th>
                <th className="px-5 py-2.5">
                  <SortableHeader
                    label="Angelegt"
                    sortKey="createdAt"
                    currentKey={table.sortKey}
                    currentDir={table.sortDir}
                    onSort={table.toggleSort}
                  />
                </th>
                <th className="px-5 py-2.5 w-px" aria-label="Aktionen" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle">
              {table.rows.map((t) => (
                <TagRow
                  key={t.id}
                  tag={t}
                  isAdmin={isAdmin}
                  selected={table.isSelected(t.id)}
                  onSelect={() => table.toggleRow(t.id)}
                />
              ))}
            </tbody>
          </table>
        )}
      </TableShell>

      <Pager
        page={table.page}
        totalPages={table.totalPages}
        onPage={table.setPage}
        filteredRows={table.filteredRows}
        pageSize={table.pageSize}
      />

      {isAdmin && (
        <BulkActionBar
          selectionCount={table.selectionCount}
          filteredRows={table.filteredRows}
          onClear={table.clearSelection}
          onSelectAllFiltered={table.selectAllFiltered}
        >
          <Button
            variant="danger"
            size="sm"
            disabled={bulkDelete.isPending}
            onClick={async () => {
              const ids = Array.from(table.selectedIds)
              const ok = await confirm({
                title: `${ids.length} Tags löschen?`,
                description:
                  'Tag-Zuordnungen an Time-Entries gehen verloren. Diese Aktion ist unumkehrbar.',
                variant: 'danger',
                confirmLabel: 'Endgültig löschen',
              })
              if (ok) bulkDelete.mutate(ids)
            }}
          >
            <Trash2 className="h-3.5 w-3.5" /> Löschen
          </Button>
        </BulkActionBar>
      )}

      <CreateTagDialog open={createOpen} onClose={() => setCreateOpen(false)} />
    </div>
  )
}

function TagRow({
  tag,
  isAdmin,
  selected,
  onSelect,
}: {
  tag: Tag
  isAdmin: boolean
  selected: boolean
  onSelect: () => void
}) {
  const queryClient = useQueryClient()
  const remove = useMutation({
    mutationFn: () => api.deleteTag(tag.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags'] })
      notify.success('Tag gelöscht')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  return (
    <tr
      className={`group transition-colors hover:bg-bg-overlay/40 ${selected ? 'bg-accent/5' : ''}`}
    >
      {isAdmin && (
        <td className="pl-5 pr-2 py-3">
          <RowCheckbox checked={selected} onToggle={onSelect} label={`Tag ${tag.name} auswählen`} />
        </td>
      )}
      <td className="px-5 py-3">
        <span
          className="inline-flex h-5 w-5 rounded-full border border-border-subtle"
          style={{ backgroundColor: tag.color }}
          aria-hidden
        />
      </td>
      <td className="px-5 py-3">
        <span
          className="inline-flex items-center rounded-full px-2.5 py-0.5 text-sm border font-medium"
          style={{
            backgroundColor: `${tag.color}1a`,
            borderColor: `${tag.color}55`,
            color: tag.color,
          }}
        >
          {tag.name}
        </span>
      </td>
      <td className="px-5 py-3 font-mono text-xs text-text-tertiary uppercase">{tag.color}</td>
      <td className="px-5 py-3 text-xs text-text-tertiary tabular-nums">
        {new Date(tag.createdAt).toLocaleDateString('de-DE')}
      </td>
      <td className="px-5 py-3">
        <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 touch:opacity-100 transition-opacity">
          {isAdmin && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label="Tag löschen"
              onClick={async () => {
                const ok = await confirm({
                  title: `Tag „${tag.name}“ löschen?`,
                  description: 'Tag-Zuordnungen an Time-Entries gehen verloren.',
                  variant: 'danger',
                  confirmLabel: 'Löschen',
                })
                if (ok) remove.mutate()
              }}
              className="text-text-tertiary hover:text-danger"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </td>
    </tr>
  )
}

function CreateTagDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [color, setColor] = useState('#FF6B35')

  const create = useMutation({
    mutationFn: () => api.createTag({ name, color }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags'] })
      notify.success('Tag angelegt')
      setName('')
      setColor('#FF6B35')
      onClose()
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return
    create.mutate()
  }

  return (
    <SidePanel
      open={open}
      onClose={onClose}
      title="Neuer Tag"
      description="Frei wählbares Label, z.B. deep-work, billable, support."
      footer={
        <>
          <Button variant="ghost" onClick={onClose} type="button">
            Abbrechen
          </Button>
          <Button onClick={onSubmit} type="button" disabled={!name.trim() || create.isPending}>
            Anlegen
          </Button>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4 pb-2">
        <div className="space-y-2">
          <Label htmlFor="tag-name">Name</Label>
          <Input
            id="tag-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="deep-work"
            autoFocus
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="tag-color">Farbe</Label>
          <div className="flex items-center gap-3">
            <input
              id="tag-color"
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="h-10 w-16 rounded-md border border-border-default bg-bg-overlay cursor-pointer"
            />
            <Input value={color} onChange={(e) => setColor(e.target.value)} className="font-mono" />
          </div>
        </div>
      </form>
    </SidePanel>
  )
}
